import { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import type { SearchRow } from '../api/contracts';
import { formatPrice } from '../lib/money';
import {
  bookServiceHref,
  dayLabel,
  isSoonest,
  salonHref,
  slotHref,
} from '../lib/slot-labels';
import {
  displayRating,
  featuredServices,
  formatKm,
  isVerified,
  reach,
  rowCity,
  type FeaturedService,
} from '../lib/salon-meta';
import { MQ, useMediaQuery } from '../lib/use-media-query';
import {
  Badge,
  Button,
  RatingStars,
  SalonAvatar,
  Skeleton,
  SlotChip,
  cn,
  focusRing,
} from '../ui';

/**
 * Salon cards for discovery (design-spec §3 "Cards", boards 01, 02, 13).
 *
 * - `SalonCard` — home grid (401 wide, 185px header) and the mobile home card
 *   (130px header, whole card is a link, sand slot chips, no buttons).
 * - `SalonResultCard` — the horizontal results-list row: ≤ 2 matching services
 *   and ≤ 3 slots under one relative day label.
 *
 * Availability-first: slot chips deep-link into the booking time step with the
 * slot preselected. Every redesign field is optional — no `nextAvailable` hides
 * the slot row, no `rating` shows "New on Salunas" (D-04, never a fake score).
 * Prices always use the row's own `currency`.
 */

export interface SalonCardProps {
  salon: SearchRow;
  /** Injected clock for specs; defaults to the real one. */
  now?: Date;
}

function useLocale(): string {
  const { i18n } = useTranslation();
  return i18n.resolvedLanguage || i18n.language || 'en';
}

/** Cover photo, else the deterministic tonal tile with the initial. */
function CardMedia({
  salon,
  initialClassName,
  className,
}: {
  salon: SearchRow;
  initialClassName: string;
  className?: string;
}) {
  if (salon.coverImageUrl) {
    return (
      <span aria-hidden="true" className={cn('block overflow-hidden bg-sl-map', className)}>
        <img
          src={salon.coverImageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          style={{ objectPosition: salon.coverImagePosition ?? undefined }}
          className="h-full w-full object-cover"
        />
      </span>
    );
  }
  return (
    <SalonAvatar
      name={salon.name}
      slug={salon.subdomain}
      size="fill"
      className={className}
      initialClassName={initialClassName}
    />
  );
}

/** "★ 4.8 (86)" or the "New on Salunas" pill. */
export function RatingOrNew({ salon, size = 'md' }: { salon: SearchRow; size?: 'sm' | 'md' }) {
  const rating = displayRating(salon);
  if (rating == null) return <Badge kind="new" size={size} />;
  return (
    <RatingStars
      value={rating}
      count={salon.reviewCount}
      className={size === 'sm' ? 'text-[13px]' : undefined}
    />
  );
}

function useReachLabel(salon: SearchRow): string {
  const { t } = useTranslation();
  const locale = useLocale();
  const r = reach(salon);
  switch (r.kind) {
    case 'travels':
      return t('search.card.travelsKm', { km: formatKm(r.km, locale) });
    case 'area':
      return t('search.card.travelsArea', { area: r.area });
    case 'travelsToYou':
      return t('search.card.travelsToYou');
    case 'distance':
      return t('search.card.distanceKm', { km: formatKm(r.km, locale) });
    default:
      return '';
  }
}

const Dot = () => (
  <span aria-hidden="true" className="text-sl-label">
    ·
  </span>
);

/** Rating · city · distance / travel radius. `country` → "Apeldoorn (NL)". */
function MetaLine({ salon, className, country }: { salon: SearchRow; className?: string; country?: string | null }) {
  const cityName = rowCity(salon);
  const city = cityName && country ? `${cityName} (${country})` : cityName;
  const reachLabel = useReachLabel(salon);
  return (
    <p className={cn('flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] text-sl-body-2', className)}>
      <RatingOrNew salon={salon} size="sm" />
      {city && (
        <>
          <Dot />
          <span>{city}</span>
        </>
      )}
      {reachLabel && (
        <>
          <Dot />
          {/* A free-text service area can be long: cap it so the salon name keeps its room. */}
          <span className="max-w-[200px] truncate tabular-nums" title={reachLabel}>
            {reachLabel}
          </span>
        </>
      )}
    </p>
  );
}

function ServiceLine({ service, currency }: { service: FeaturedService; currency?: string }) {
  const { t } = useTranslation();
  const price = formatPrice(service.price, currency);
  return (
    <div className="flex items-baseline justify-between gap-3 text-[14px] text-sl-ink">
      <span className="min-w-0 truncate">
        {service.name}
        <span aria-hidden="true" className="text-sl-label"> · </span>
        <span className="text-sl-body-2">{t('search.card.minutes', { count: service.durationMinutes })}</span>
      </span>
      <span className="shrink-0 font-semibold tabular-nums">
        {service.priceFrom ? t('search.card.fromPrice', { price }) : price}
      </span>
    </div>
  );
}

/** The salon's name as a stretched link: the whole card opens the salon. */
function NameLink({ salon, className, state }: { salon: SearchRow; className?: string; state?: unknown }) {
  return (
    <Link
      to={salonHref(salon.subdomain)}
      state={state}
      className={cn(
        'rounded-sm text-sl-ink after:absolute after:inset-0 after:rounded-[inherit] after:content-[""] hover:text-sl-link',
        focusRing,
        className,
      )}
    >
      {salon.name}
    </Link>
  );
}

// ─── Home grid card ────────────────────────────────────────────────────

export function SalonCard({ salon, now }: SalonCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const desktop = useMediaQuery(MQ.md);
  const [service] = featuredServices(salon, 1);
  const next = salon.nextAvailable;
  const slots = next?.slots?.filter(Boolean) ?? [];
  const showSlots = !!next && slots.length > 0 && !!next.serviceId;
  const verified = isVerified(salon);
  const city = rowCity(salon);
  const reachLabel = useReachLabel(salon);

  const chips = showSlots
    ? slots.slice(0, desktop ? 2 : 3).map((time, i) => {
        const day = dayLabel(next.date, next.timezone, t, locale, { now });
        // Desktop labels every chip ("Today 16:00"); mobile only the first.
        const label = desktop || i === 0 ? `${day} ${time}` : time;
        return (
          <SlotChip
            key={time}
            to={slotHref(salon.subdomain, next.serviceId, next.date, time)}
            label={label}
            tone={desktop ? 'outline' : 'sand'}
            soonest={isSoonest(i, next.date, next.timezone, now)}
            ariaLabel={t('search.card.slotAria', { when: `${day} ${time}`, salon: salon.name })}
            className="relative z-10"
          />
        );
      })
    : null;

  return (
    <article
      data-testid="salon-card"
      className="relative flex h-full flex-col overflow-hidden rounded-18 border border-sl-line bg-sl-surface transition-shadow hover:shadow-book-card md:rounded-20"
    >
      <div className="relative h-[130px] md:h-[185px]">
        <CardMedia
          salon={salon}
          className="h-full w-full"
          initialClassName="text-[44px] opacity-100 md:text-[86px] md:opacity-50"
        />
        {salon.isMobile && (
          <Badge
            kind="mobile"
            tone={desktop ? 'photo' : 'photoCheck'}
            className="absolute left-3 top-3"
          />
        )}
      </div>

      <div className="flex flex-1 flex-col px-4 pb-4 pt-3.5 md:p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="flex min-w-0 items-center gap-1.5 text-[16px] font-semibold leading-tight md:text-[17px]">
            <NameLink salon={salon} className="truncate" />
            {verified && <Badge kind="verified" size="sm" />}
          </h3>
          {!desktop && <RatingOrNew salon={salon} size="sm" />}
        </div>

        {desktop ? (
          <>
            <MetaLine salon={salon} className="mt-2" />
            {service && (
              <div className="mt-3">
                <ServiceLine service={service} currency={salon.currency} />
              </div>
            )}
          </>
        ) : (
          <p className="mt-1.5 truncate text-[13px] text-sl-body-2">
            {[city, reachLabel, service ? t('search.card.fromPrice', { price: formatPrice(service.price, salon.currency) }) : '']
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}

        {showSlots && (
          <div className="mt-3.5">
            {desktop && (
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[1.3px] text-sl-label-2">
                {t('search.card.nextAvailable')}
              </p>
            )}
            <div className="flex flex-wrap gap-2">{chips}</div>
          </div>
        )}

        {desktop && (
          <div className="relative z-10 mt-auto grid grid-cols-2 gap-2.5 pt-5">
            <Button to={salonHref(salon.subdomain)} variant="outline" className="h-ctl-md w-full">
              {t('search.card.viewSalon')}
            </Button>
            <Button to={bookServiceHref(salon.subdomain)} variant="dark" className="w-full">
              {t('search.card.bookNow')}
            </Button>
          </div>
        )}
      </div>
    </article>
  );
}

// ─── Results-list card ─────────────────────────────────────────────────

export interface SalonResultCardProps extends SalonCardProps {
  /** Rows from `furtherAfield`: dimmed, outline "View salon". */
  furtherAfield?: boolean;
  /** Highlighted from the map (pin hover). */
  active?: boolean;
  /**
   * The search URL (pathname + search) this card was shown on. Passed as router
   * state `{ fromSearch }` on every salon/booking link so the salon page's
   * "← All results" returns to the exact search.
   */
  fromSearch?: string;
  onActiveChange?: (key: string | null) => void;
  /**
   * The search's own country (domain, else the primary rows'). A further-afield
   * row in another country shows it after the city: "Apeldoorn (NL)".
   */
  homeCountry?: string | null;
}

/**
 * Memoised (M3 QA item 5): a hover elsewhere on the page must not re-render
 * every card. Pass stable props (`onActiveChange` from the highlight store).
 */
export const SalonResultCard = memo(function SalonResultCard({
  salon,
  now,
  furtherAfield = false,
  active = false,
  onActiveChange,
  fromSearch,
  homeCountry,
}: SalonResultCardProps) {
  const linkState = fromSearch ? { fromSearch } : undefined;
  const { t } = useTranslation();
  const locale = useLocale();
  const services = featuredServices(salon, 2);
  const next = salon.nextAvailable;
  const slots = next?.slots?.filter(Boolean).slice(0, 3) ?? [];
  const showSlots = !!next && slots.length > 0 && !!next.serviceId;
  const verified = isVerified(salon);
  const day = showSlots ? dayLabel(next.date, next.timezone, t, locale, { now, style: 'long' }) : '';
  const shortDay = showSlots ? dayLabel(next.date, next.timezone, t, locale, { now }) : '';
  const primaryBook = showSlots && !furtherAfield;
  // Results sit under the page h1; further-afield rows under their h2 divider (M7 axe heading-order).
  const Heading = furtherAfield ? 'h3' : 'h2';

  return (
    <article
      data-testid="result-card"
      data-further={furtherAfield || undefined}
      onMouseEnter={() => onActiveChange?.(salon.key)}
      onMouseLeave={() => onActiveChange?.(null)}
      onFocus={() => onActiveChange?.(salon.key)}
      onBlur={() => onActiveChange?.(null)}
      className={cn(
        'relative flex gap-3.5 rounded-18 border bg-sl-surface p-3 transition-colors md:gap-[18px] md:p-4',
        active ? 'border-sl-accent' : 'border-sl-line hover:border-sl-line-strong',
        furtherAfield && 'opacity-[.92]',
      )}
    >
      <div className="relative h-[72px] w-[72px] shrink-0 md:h-[150px] md:w-[168px]">
        <CardMedia
          salon={salon}
          className="h-full w-full rounded-12"
          initialClassName="text-[32px] md:text-[60px]"
        />
        {salon.isMobile && (
          <Badge kind="mobile" tone="photo" size="sm" className="absolute left-2 top-2 hidden md:inline-flex" />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between md:gap-3">
          <Heading className="flex min-w-0 items-center gap-1.5 text-[16px] font-semibold leading-tight md:max-w-[60%] md:shrink-0 md:text-[17px]">
            <NameLink salon={salon} className="truncate" state={linkState} />
            {verified && <Badge kind="verified" size="sm" />}
          </Heading>
          <MetaLine
            salon={salon}
            country={
              furtherAfield && salon.country && homeCountry && salon.country.toUpperCase() !== homeCountry.toUpperCase()
                ? salon.country.toUpperCase()
                : null
            }
            className="md:min-w-0 md:flex-1 md:justify-end md:text-right"
          />
        </div>
        {salon.isMobile && (
          <Badge kind="mobile" tone="soft" size="sm" className="mt-1.5 self-start md:hidden" />
        )}

        {services.length > 0 && (
          <div className="mt-2 flex flex-col gap-1">
            {services.map((s) => (
              <ServiceLine key={s.id ?? s.name} service={s} currency={salon.currency} />
            ))}
          </div>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-2.5 pt-3">
          {showSlots && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[1.3px] text-sl-label-2">{day}</span>
              {slots.map((time, i) => (
                <SlotChip
                  key={time}
                  to={slotHref(salon.subdomain, next.serviceId, next.date, time)}
                  label={time}
                  state={linkState}
                  soonest={isSoonest(i, next.date, next.timezone, now)}
                  ariaLabel={t('search.card.slotAria', { when: `${shortDay} ${time}`, salon: salon.name })}
                  className="relative z-10"
                />
              ))}
            </div>
          )}
          <div className="relative z-10 ml-auto">
            {primaryBook ? (
              <Button asChild variant="dark" className="h-[42px]">
                <Link to={bookServiceHref(salon.subdomain)} state={linkState}>
                  {t('search.card.bookNow')}
                </Link>
              </Button>
            ) : (
              <Button asChild variant="outline" className="h-[42px]">
                <Link to={salonHref(salon.subdomain)} state={linkState}>
                  {t('search.card.viewSalon')}
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
});

// ─── Skeletons ─────────────────────────────────────────────────────────

export function SalonCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-20 border border-sl-line bg-sl-surface">
      <Skeleton radius={12} className="h-[130px] w-full rounded-none md:h-[185px]" />
      <div className="space-y-3 p-5">
        <Skeleton radius="pill" className="h-4 w-2/3" />
        <Skeleton radius="pill" className="h-3.5 w-1/2" />
        <Skeleton radius="pill" className="h-7 w-3/4" />
      </div>
    </div>
  );
}

export function SalonResultCardSkeleton() {
  return (
    <div className="flex gap-4 rounded-18 border border-sl-line bg-sl-surface p-3 md:p-4">
      <Skeleton radius={12} className="h-[72px] w-[72px] shrink-0 md:h-[150px] md:w-[168px]" />
      <div className="flex flex-1 flex-col gap-3 py-1">
        <Skeleton radius="pill" className="h-4 w-1/2" />
        <Skeleton radius="pill" className="h-3.5 w-3/4" />
        <Skeleton radius="pill" className="mt-auto h-7 w-2/3" />
      </div>
    </div>
  );
}

/** "→" for forward text links ("See all salons →"). */
export function ForwardArrow() {
  return <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden="true" />;
}
